/**
 * NotificationBell.js
 * Shows alerts for:
 *  - Critical/High vulnerabilities on endpoints
 *  - Failed patches that need immediate attention
 *  - Devices offline for > 24 hours
 *  - Patches pending > 24 hours (stuck)
 * Each alert has a "Deploy Now" or "View" action button.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

function getHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('adminToken')}` };
}

function fmtAgo(d) {
  if (!d) return '';
  const m = Math.floor((Date.now() - new Date(d)) / 60000);
  if (m < 60)  return `${m}m ago`;
  if (m < 1440) return `${Math.floor(m/60)}h ago`;
  return `${Math.floor(m/1440)}d ago`;
}

const SEVERITY_COLOR = { critical: '#dc2626', high: '#ea580c', medium: '#d97706' };
const SEVERITY_BG    = { critical: '#fee2e2', high: '#ffedd5', medium: '#fef3c7' };

export default function NotificationBell({ onNavigate }) {
  const [open,    setOpen]    = useState(false);
  const [alerts,  setAlerts]  = useState([]);
  const [loading, setLoading] = useState(false);
  const [deploying, setDeploying] = useState(null);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    function handle(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const loadAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const [vulnRes, compRes, patchRes] = await Promise.all([
        axios.get('/api/compliance/vulnerabilities?status=open', { headers: getHeaders() }),
        axios.get('/api/compliance/summary',                     { headers: getHeaders() }),
        axios.get('/api/patches/compliance',                     { headers: getHeaders() })
      ]);

      const newAlerts = [];

      // ── Critical/High vulnerabilities ────────────────────────────────────
      const critVulns = (vulnRes.data || []).filter(v => v.severity === 'critical' || v.severity === 'high');
      if (critVulns.length > 0) {
        const bySeverity = critVulns.reduce((a, v) => { a[v.severity] = (a[v.severity]||0)+1; return a; }, {});
        newAlerts.push({
          id:       'vulns',
          type:     'critical',
          icon:     '⚠',
          title:    `${critVulns.length} Vulnerable Asset${critVulns.length > 1 ? 's' : ''}`,
          body:     Object.entries(bySeverity).map(([s,c]) => `${c} ${s}`).join(', ') +
                    ` — immediate patching required`,
          action:   'View Vulnerabilities',
          page:     'pvc',
          severity: critVulns.some(v => v.severity === 'critical') ? 'critical' : 'high',
          items:    critVulns.slice(0, 3).map(v => ({
            label: `${v.device?.hostname || 'Unknown'} — ${v.affectedProduct}`,
            sub:   v.title,
            severity: v.severity
          }))
        });
      }

      // ── Failed patches ────────────────────────────────────────────────────
      const failed = (patchRes.data?.details || []).filter(s => s.status === 'failed');
      if (failed.length > 0) {
        newAlerts.push({
          id:       'failed',
          type:     'warning',
          icon:     '✘',
          title:    `${failed.length} Failed Patch${failed.length > 1 ? 'es' : ''}`,
          body:     `${failed.length} patch deployment${failed.length > 1 ? 's' : ''} failed and need attention`,
          action:   'Reset & Retry',
          actionType: 'reset-failed',
          severity: 'high',
          items:    failed.slice(0, 3).map(s => ({
            label:    `${s.device?.hostname || '?'} — ${s.patch?.name || '?'}`,
            sub:      (s.error || '').substring(0, 60),
            severity: 'high',
            statusId: s.id,
            patchId:  s.patch?.id,
            deviceId: s.device?.id
          }))
        });
      }

      // ── Devices offline > 24h ─────────────────────────────────────────────
      const devices   = compRes.data?.devices || [];
      const longOffline = devices.filter(d => {
        if (!d.device?.lastHeartbeat) return false;
        const hrs = (Date.now() - new Date(d.device.lastHeartbeat)) / 3600000;
        return hrs > 24;
      });
      if (longOffline.length > 0) {
        newAlerts.push({
          id:       'offline',
          type:     'info',
          icon:     '○',
          title:    `${longOffline.length} Device${longOffline.length > 1 ? 's' : ''} Offline > 24h`,
          body:     'These devices are not receiving patches or compliance updates',
          action:   'View Endpoints',
          page:     'connections',
          severity: 'medium',
          items:    longOffline.slice(0, 3).map(d => ({
            label: d.device?.hostname || '?',
            sub:   `Last seen: ${fmtAgo(d.device?.lastHeartbeat)}`,
            severity: 'medium'
          }))
        });
      }

      // ── Non-compliant devices ─────────────────────────────────────────────
      const nonCompliant = devices.filter(d => d.overallStatus === 'non_compliant');
      if (nonCompliant.length > 0) {
        newAlerts.push({
          id:       'noncompliant',
          type:     'warning',
          icon:     '⛨',
          title:    `${nonCompliant.length} Non-Compliant Device${nonCompliant.length > 1 ? 's' : ''}`,
          body:     'Deploy critical patches immediately to restore compliance',
          action:   'Deploy Critical Patches',
          actionType: 'enforce-critical',
          severity: 'high',
          items:    nonCompliant.slice(0, 3).map(d => ({
            label: d.device?.hostname || '?',
            sub:   `Score: ${d.overallScore}%`,
            severity: 'high'
          }))
        });
      }

      setAlerts(newAlerts);
    } catch (e) {
      console.error('[notifications] load error:', e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load on open, refresh every 5 min
  useEffect(() => {
    loadAlerts();
    const t = setInterval(loadAlerts, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [loadAlerts]);

  async function handleAction(alert) {
    if (alert.page) {
      onNavigate(alert.page);
      setOpen(false);
      return;
    }

    if (alert.actionType === 'reset-failed') {
      setDeploying(alert.id);
      try {
        // Reset all failed patches to pending
        await axios.post('/api/tasks/enforce',
          { severity: ['critical','high','medium'], onlineOnly: false },
          { headers: getHeaders() }
        );
        await loadAlerts();
      } catch (e) { console.error(e.message); }
      setDeploying(null);
    }

    if (alert.actionType === 'enforce-critical') {
      setDeploying(alert.id);
      try {
        await axios.post('/api/tasks/enforce',
          { severity: ['critical','high'], onlineOnly: true },
          { headers: getHeaders() }
        );
        await loadAlerts();
      } catch (e) { console.error(e.message); }
      setDeploying(null);
    }
  }

  const totalAlerts = alerts.length;
  const criticalCount = alerts.filter(a => a.severity === 'critical').length;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Bell button */}
      <div
        className="topbar-icon-btn"
        onClick={() => { setOpen(o => !o); if (!open) loadAlerts(); }}
        title={totalAlerts > 0 ? `${totalAlerts} alert${totalAlerts > 1 ? 's' : ''}` : 'No alerts'}
        style={{ cursor: 'pointer' }}
      >
        🔔
        {totalAlerts > 0 && (
          <span style={{
            position: 'absolute', top: -5, right: -5,
            background: criticalCount > 0 ? '#dc2626' : '#f59e0b',
            color: 'white', fontSize: 9, fontWeight: 700,
            width: 18, height: 18, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '2px solid #fff'
          }}>
            {totalAlerts > 9 ? '9+' : totalAlerts}
          </span>
        )}
      </div>

      {/* Dropdown panel */}
      {open && (
        <div style={{
          position: 'absolute', top: 42, right: 0,
          width: 400, maxHeight: 520, overflowY: 'auto',
          background: '#fff', border: '1px solid #e8ecf4',
          borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          zIndex: 999
        }}>
          {/* Header */}
          <div style={{
            padding: '14px 16px 10px', borderBottom: '1px solid #f0f4ff',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#1e2a3b' }}>
              Alerts & Actions
              {totalAlerts > 0 && (
                <span style={{
                  marginLeft: 8, background: '#fee2e2', color: '#dc2626',
                  fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 10
                }}>
                  {totalAlerts}
                </span>
              )}
            </div>
            <button
              onClick={loadAlerts}
              style={{ background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 12, color: '#94a3b8' }}>
              {loading ? '⟳' : '↻ Refresh'}
            </button>
          </div>

          {/* Alert list */}
          {loading && alerts.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
              Loading alerts...
            </div>
          ) : alerts.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>✅</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1e2a3b' }}>All Clear</div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                No vulnerabilities or failed patches detected
              </div>
            </div>
          ) : (
            <div>
              {alerts.map(alert => (
                <div key={alert.id} style={{
                  borderBottom: '1px solid #f0f4ff',
                  padding: '14px 16px'
                }}>
                  {/* Alert header */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                      background: SEVERITY_BG[alert.severity] || '#f1f5f9',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 16, color: SEVERITY_COLOR[alert.severity] || '#64748b'
                    }}>
                      {alert.icon}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#1e2a3b' }}>
                        {alert.title}
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, lineHeight: 1.4 }}>
                        {alert.body}
                      </div>
                    </div>
                  </div>

                  {/* Sub-items */}
                  {alert.items?.length > 0 && (
                    <div style={{ marginBottom: 10, paddingLeft: 42 }}>
                      {alert.items.map((item, i) => (
                        <div key={i} style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          padding: '4px 0', borderBottom: i < alert.items.length - 1 ? '1px solid #f8fafc' : 'none'
                        }}>
                          <span style={{
                            width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                            background: SEVERITY_COLOR[item.severity] || '#94a3b8'
                          }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 500, color: '#374151',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {item.label}
                            </div>
                            {item.sub && (
                              <div style={{ fontSize: 10, color: '#94a3b8',
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {item.sub}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Action button */}
                  <div style={{ paddingLeft: 42 }}>
                    <button
                      onClick={() => handleAction(alert)}
                      disabled={deploying === alert.id}
                      style={{
                        background: SEVERITY_COLOR[alert.severity] || '#1a56db',
                        color: '#fff', border: 'none', cursor: 'pointer',
                        borderRadius: 6, padding: '6px 14px', fontSize: 12,
                        fontWeight: 600, opacity: deploying === alert.id ? 0.7 : 1
                      }}
                    >
                      {deploying === alert.id ? '⟳ Processing...' : alert.action}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Footer */}
          <div style={{ padding: '10px 16px', borderTop: '1px solid #f0f4ff',
            fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>
            Auto-refreshes every 5 minutes
          </div>
        </div>
      )}
    </div>
  );
}
